/** Сбор контекста для предусловий `when` из существующих доменных хуков (ТЗ §3.2). */

import { useSyncExternalStore } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { useDeletedMailboxes, useMailboxes, useQuota } from '../mailbox/api';
import { useForwardingReady } from '../forwarding/useForwardingReady';
import type { OnboardingContext } from './engine/predicates';

const TABLET_MIN = 768;
const DESKTOP_MIN = 1024;

function readBreakpoint(): OnboardingContext['breakpoint'] {
  if (typeof window === 'undefined') return 'desktop';
  if (window.innerWidth >= DESKTOP_MIN) return 'desktop';
  return window.innerWidth >= TABLET_MIN ? 'tablet' : 'mobile';
}

function subscribeToResize(onChange: () => void): () => void {
  window.addEventListener('resize', onChange);
  return () => {
    window.removeEventListener('resize', onChange);
  };
}

export function useBreakpoint(): OnboardingContext['breakpoint'] {
  return useSyncExternalStore(subscribeToResize, readBreakpoint, () => 'desktop' as const);
}

/**
 * Все запросы читаются в режиме `enabled: false` — из кеша, который наполняют сами страницы.
 * Онбординг не должен добавлять сетевых запросов на экраны, где тур не показывается.
 */
export function useOnboardingContext(): OnboardingContext {
  const mailboxes = useMailboxes(false);
  const deleted = useDeletedMailboxes(false);
  const quota = useQuota(false);
  const forwardingReady = useForwardingReady(false);
  const breakpoint = useBreakpoint();
  const route = useRouterState({ select: (s) => s.location.pathname });

  const items = mailboxes.data?.items ?? [];
  const traffic = quota.data?.traffic;

  return {
    mailboxCount: items.length,
    pinnedCount: mailboxes.data?.pinnedItems.length ?? 0,
    deletedCount: deleted.data?.items.length ?? 0,
    trafficUsedPercent:
      traffic && traffic.limitMb > 0 ? Math.round((traffic.usedMb / traffic.limitMb) * 100) : 0,
    atMailboxLimit: quota.data ? quota.data.active.used >= quota.data.active.limit : false,
    forwardingReady,
    plan: quota.data?.plan ?? 'free',
    breakpoint,
    route,
  };
}
